import React, { useEffect, useState } from "react";
import { Button, Dimmer, Icon, Loader, Message } from "semantic-ui-react";
import { debugLog, debugWarn } from "../helpers/debug";

const isDev = process.env.NODE_ENV === "development";

/**
 * LoadingWithTimeout - A loading component that shows an error state after a timeout
 * Prevents infinite loading spinners by providing users with recovery options
 *
 * @param {boolean} loading - Whether the component should show loading state
 * @param {number} timeout - Milliseconds before showing timeout error (default: 15000)
 * @param {function} onRetry - Custom retry handler (defaults to page reload)
 * @param {boolean} inverted - Whether to invert the dimmer colors
 * @param {string} loadingText - Custom loading message
 * @param {string} context - Description of what's loading (for dev logging)
 * @param {React.ReactNode} children - Content to render when not loading
 */
const LoadingWithTimeout = ({
  loading = true,
  timeout = 15000,
  onRetry,
  inverted = false,
  loadingText = "Loading...",
  context = "Unknown",
  children,
}) => {
  const [timedOut, setTimedOut] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      setElapsedTime(0);
      return;
    }

    debugLog(`[LoadingWithTimeout] Started loading: ${context}`);

    const startTime = Date.now();

    // Update elapsed time every second (dev only)
    const intervalId = isDev
      ? setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setElapsedTime(elapsed);

          if (elapsed > 0 && elapsed % 5 === 0) {
            debugLog(
              `[LoadingWithTimeout] Still loading ${context}: ${elapsed}s elapsed`
            );
          }
        }, 1000)
      : null;

    const timer = setTimeout(() => {
      debugWarn(
        `[LoadingWithTimeout] TIMEOUT reached for: ${context} after ${timeout}ms`
      );

      setTimedOut(true);
    }, timeout);

    return () => {
      clearTimeout(timer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    loading,
    timeout,
    context,
  ]);

  const handleRetry = () => {
    debugLog(`[LoadingWithTimeout] Retry clicked for: ${context}`);
    setTimedOut(false);
    setElapsedTime(0);

    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleClearAndLogout = () => {
    debugLog("[LoadingWithTimeout] Clearing storage and redirecting to login");
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  if (timedOut) {
    return (
      <div
        style={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: "50vh",
          padding: "2em",
          textAlign: "center",
        }}
      >
        <Message
          style={{
            maxWidth: "500px",
          }}
          warning
        >
          <Message.Header>
            <Icon name="clock outline" />
            Loading is taking longer than expected
          </Message.Header>
          <p
            style={{
              marginTop: "1em",
            }}
          >
            There may be a connection issue or the data is temporarily
            unavailable.
          </p>

          {isDev && (
            <p
              style={{
                color: "#666",
                fontSize: "0.85em",
                marginTop: "0.5em",
              }}
            >
              <strong>Dev Info:</strong> Context: {context}, Timeout: {timeout}
              ms
            </p>
          )}
        </Message>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1em",
            justifyContent: "center",
            marginTop: "1.5em",
          }}
        >
          <Button
            color="yellow"
            onClick={handleRetry}
            size="large"
          >
            <Icon name="refresh" />
            Try Again
          </Button>

          <Button
            basic
            onClick={handleClearAndLogout}
          >
            <Icon name="sign out" />
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <Dimmer
        active
        inverted={inverted}
      >
        <Loader>
          {loadingText}
          {isDev && elapsedTime > 3 && (
            <span
              style={{
                display: "block",
                fontSize: "0.8em",
                marginTop: "0.5em",
              }}
            >
              ({elapsedTime}s - {context})
            </span>
          )}
        </Loader>
      </Dimmer>
    );
  }

  return children || null;
};

export default LoadingWithTimeout;
